import { SafeToIntersectString } from 'types';

import { quickSwitch } from 'utils/conditions';
import { storage } from 'utils/storage';

type BaseColors = 'primary' | 'secondary' | 'success' | 'destructive' | 'warning' | 'info';

type BackgroundOnlyColors = 'popover' | 'muted';

type WithForeground<Color extends string> = Color | `${Color}-foreground`;

// add lighter/darker?
type WithLightLevels<Color extends string> = Color | `${Color}-light` | `${Color}-dark`;

type PaintColors = WithForeground<BaseColors> | WithLightLevels<BaseColors>;

type BackgroundColors = WithForeground<BackgroundOnlyColors>;

// verify = border
type UniqueColors = 'input' | 'hover' | 'border';

type DirectColors = 'background' | 'foreground';

export type Color = PaintColors | BackgroundColors | UniqueColors | DirectColors;

export type ColorOrCustom = Color | SafeToIntersectString;

function getForegroundColor(color: string): string {
  if (color.includes('foreground')) return color;

  const modifiers = ['light', 'dark'];

  const purged = modifiers.reduce((acc, next) => acc.replace(`-${next}`, ''), color);

  return `${purged}-foreground`;
}

// colors that are the final form on our variable, not needing to hsl() it
// we should revise implementation to see if we should just add all hsl() calls to variables
const fullColors: Color[] = ['hover'];

const allColors = [
  'background',
  'foreground',
  'input',

  ...fullColors,

  ...['popover'].map((color) => [color, getForegroundColor(color)]).flat(),

  ...['primary', 'secondary', 'success', 'destructive', 'warning', 'info', 'muted']
    .map((color) => [color, `${color}-light`, `${color}-dark`, getForegroundColor(color)])
    .flat(),
].flat() as Color[];

function isThemeColor(color: string): boolean {
  return allColors.includes(color as Color);
}

export function resolveColor(color: ColorOrCustom): string {
  if (fullColors.includes(color as Color)) return `var(--${color})`;

  if (isThemeColor(color)) return `hsla(var(--${color}))`;

  return color;
}

export function resolveForegroundColor(color: ColorOrCustom): string {
  return isThemeColor(color) ? resolveColor(getForegroundColor(color)) : '#fff';
}

export function resolveColorVariant(color: ColorOrCustom, variant: 'light' | 'dark'): string {
  if (isThemeColor(color) && !color.includes(variant)) return resolveColor(`${color}-${variant}`);

  return color;
}

type ColorTarget = 'bg' | 'text' | 'border';

export function resolveColorClass(color: ColorOrCustom, target: ColorTarget = 'bg'): string {
  const key = `${target}-${color}`;

  if (fullColors.includes(color as Color) || isThemeColor(color)) return key;

  const createdClass = storage.memory.get<string>(key);

  if (createdClass) return createdClass;

  const style = document.createElement('style');

  // this needs to be consistent to work
  const className = key.replace(/[^a-zA-Z0-9-]/g, '');

  const css = quickSwitch(target, [
    { is: 'bg', then: `background-color: ${color};` },
    { is: 'text', then: `color: ${color};` },
    { is: 'border', then: `border-color: ${color};` },
  ]);

  style.innerHTML = `
    .${className} {
      ${css}
    }
  `;

  document.head.appendChild(style);

  storage.memory.set(key, className);

  return className;
}

export function resolveForegroundColorClass(
  color: ColorOrCustom,
  target: ColorTarget = 'text',
): string {
  const name = isThemeColor(color) ? getForegroundColor(color) : `${target}-white`;

  return `${target}-${name}`;
}

export const COLORS = {
  border: 'hsl(var(--border))',

  input: 'hsl(var(--input))',

  ring: 'hsl(var(--ring))',

  background: 'hsl(var(--background))',

  foreground: {
    DEFAULT: 'hsl(var(--foreground))',
    light: 'hsl(var(--foreground-light))',
    title: 'hsl(var(--foreground-title))',
    title2: 'hsl(var(--foreground-title2))',
  },

  primary: {
    DEFAULT: 'hsl(var(--primary))',
    dark: 'hsl(var(--primary-dark))',
    light: 'hsl(var(--primary-light))',
    foreground: 'hsl(var(--primary-foreground))',
  },

  secondary: {
    DEFAULT: 'hsl(var(--secondary))',
    dark: 'hsl(var(--secondary-dark))',
    light: 'hsl(var(--secondary-light))',
    foreground: 'hsl(var(--secondary-foreground))',
  },

  destructive: {
    DEFAULT: 'hsl(var(--destructive))',
    dark: 'hsl(var(--destructive-dark))',
    light: 'hsl(var(--destructive-light))',
    foreground: 'hsl(var(--destructive-foreground))',
  },

  info: {
    DEFAULT: 'hsl(var(--info))',
    dark: 'hsl(var(--info-dark))',
    light: 'hsl(var(--info-light))',
    foreground: 'hsl(var(--info-foreground))',
  },

  warning: {
    DEFAULT: 'hsl(var(--warning))',
    dark: 'hsl(var(--warning-dark))',
    light: 'hsl(var(--warning-light))',
    foreground: 'hsl(var(--warning-foreground))',
  },

  success: {
    DEFAULT: 'hsl(var(--success))',
    dark: 'hsl(var(--success-dark))',
    light: 'hsl(var(--success-light))',
    foreground: 'hsl(var(--success-foreground))',
  },

  muted: {
    DEFAULT: 'hsl(var(--muted))',
    foreground: 'hsl(var(--muted-foreground))',
  },

  accent: {
    DEFAULT: 'hsl(var(--accent))',
    foreground: 'hsl(var(--accent-foreground))',
  },

  hover: {
    DEFAULT: 'var(--hover)',
  },

  popover: {
    DEFAULT: 'hsl(var(--popover))',
    foreground: 'hsl(var(--popover-foreground))',
  },

  card: {
    DEFAULT: 'hsl(var(--card))',
    foreground: 'hsl(var(--card-foreground))',
  },
};
